import { useState } from 'react';
import { PATHS } from '@client/routes';
import {
  Actions,
  Async,
  F1,
  TMap,
  Typed,
  TypedValue,
} from '../../helpers/types';
import {
  UseCollectionsModelParams,
  useCollectionsModel,
  CollectionsDispatcher,
} from './collections.model';
import {
  CollectionsModel,
  LiteCollection,
  LiteCollectionType,
} from './collections.domain';
import { Option } from '@client/new/helpers/types';
import _ from 'lodash';
import { SmartTableProps } from '@client/new/components/SmartTable/SmartTable';
import { useTabHash } from '@client/new/hooks/useTabHash';
import { getCurrentPath } from '@client/helpers/routes';
import {
  getEmptyState,
  getProgramLinks,
  getSubLinks,
  toCollectionsMode,
} from './collections.mappers';
import { useCollectionDuplicationVM } from './collectionDuplication.viewModel';
import { ExclusivePriceSetupProps } from '@client/new/common/selfServeExclusive/exclusivePriceSetup';
import {
  ExclusiveViewModel,
  useExclusiveViewModel,
} from '../../common/selfServeExclusive/exclusive.viewModel';
import { toExclusiveSettingsView } from '@client/new/common/selfServeExclusive/mappers';
import auth from '@client/components/auth';
import {
  UploadProgramModel,
  useUploadProgramModel,
} from '@client/new/pages/collections/uploadProgram.model';

export type ListType = LiteCollectionType | 'all';

export type ViewMode =
  | { type: 'empty' }
  | { type: 'drafts-only'; value: SmartTableProps<number> }
  | { type: 'standard'; value: SmartTableProps<number> };

export type CollectionsViewModel = {
  mode: ViewMode;
  listOptions: Array<Option<ListType>>;
  selectedList: ListType;
  dispatch: CollectionViewDispatcher;
  programDuplication: {
    hasError: boolean;
  };
  influencerId: number;
  createLink: string;
  emptyState: {
    otp: {
      isGuideOpen: boolean;
      closeGuide: () => void;
      openGuide: () => void;
      name: string;
      guideLink: string;
    };
    subscription: {
      name: string;
      guideLink: string;
      create: () => void;
      modal: {
        isOpen: boolean;
        onClose: () => void;
        createProgram: () => void;
        savePricing: (callback?: () => void) => void;
        exclusive: ExclusivePriceSetupProps;
        errors?: string[];
        onHelpClick?: (url: string) => void;
      };
    };
  };
  showCreate: boolean;
  selectionModal: {
    open: boolean;
    close: () => void;
  };
  subscription: {
    exclusive: ExclusivePriceSetupProps & {
      save: () => void;
      errors?: string[];
      submitting: boolean;
      onHelpClick: (url: string) => void;
      promotionsPath: string;
      hideSave: boolean;
    };
  };
  linksModal: Modal<LinksModalData>;
  subLinksModal: Modal<Pick<LinksModalData, 'links'>> & {
    showButton: boolean;
  };
  programUploadModal: Modal<
    Pick<UploadProgramModel, 'uploadSummary' | 'step'>
  > & {
    data: {
      uploadSummary: UploadProgramModel['uploadSummary'];
      step: UploadProgramModel['step'];
    };
    onDrop: (file: File) => void;
    onSubmit: () => void;
  };
  archiveModal: {
    isOpen: boolean;
    close: () => void;
    archive: () => void;
  };
  deleteModal: {
    isOpen: boolean;
    close: () => void;
    onDelete: () => void;
  };
  subscriptionSettingsModal: {
    isOpen: boolean;
    close: () => void;
  };
  confirmExitModal: {
    isOpen: boolean;
    close: () => void;
    cancel: () => void;
  };
  showSubscriptionSettings: boolean;
  showExperimentalProgramUpload: boolean;
};

type Modal<T> = {
  isOpen: boolean;
  onClose: () => void;
  data?: T;
};

type LinksModalData = {
  programName: string;
  links: Array<{ title: string; url: string; description: string }>;
};

export const listTitles: TMap<ListType, string> = {
  all: 'All',
  archived: 'Archived',
  draft: 'Drafts',
  published: 'Published',
};

const optionTypes: ListType[] = ['all', 'draft', 'published', 'archived'];

export const toListOptions = (): CollectionsViewModel['listOptions'] => {
  return optionTypes.map((value) => ({ value, label: listTitles[value] }));
};

type CollectionsType = 'program' | 'category';

export type CollectionsViewActionsMap = {
  selectList: ListType;
  create: { type: CollectionsType };
  edit: number;
  archiveProgram: number;
  deleteProgram: number;
  publishProgram: number;
  reorder: Array<number>;
  openGuide: null;
  closeGuide: null;
  selectSub: null;
  learnPPC: null;
  learnSub: null;
  closeSelectionModal: null;
  closeModal: null;
  linksModal: LiteCollection;
  subLinksModal: null;
  archiveModal: number;
  deleteModal: number;
  duplicateProgram: number;
  retryDuplication: null;
  subscriptionSettingsModal: null;
  closeSubscriptionSettingsModal: null;
  confirmExitModal: null;
  closeConfirmExitModal: null;
  openInitialPriceModal: null;
  openProgramUploadModal: null;
  uploadProgram: { file: File };
  confirmProgramUpload: null;
};

export type CollectionsViewActions = Actions<CollectionsViewActionsMap>;

export type CollectionsViewState = Pick<CollectionsViewModel, 'selectedList'>;

export type CollectionViewDispatcher = F1<CollectionsViewActions>;
export type Model = CollectionsModel & { dispatch: CollectionsDispatcher };

const getDispatcher =
  (p: {
    asyncModel: Async<Model>;
    asyncExclusiveModel: Async<ExclusiveViewModel>;
    setTabHash: (v: ListType) => void;
    setOpenModal: (modal: ModalType) => void;
    onDuplicateProgram: F1<number, void>;
    setOpenConfirmationModal: (open: boolean) => void;
    programUploadModel: UploadProgramModel;
  }): CollectionViewDispatcher =>
  (action) => {
    const {
      asyncModel,
      asyncExclusiveModel,
      setTabHash,
      setOpenModal,
      setOpenConfirmationModal,
    } = p;
    if (asyncModel.type !== 'fetched' || asyncExclusiveModel.type !== 'fetched')
      return;
    const model = asyncModel.value;
    const exclusiveModel = asyncExclusiveModel.value;

    switch (action.type) {
      case 'selectList':
        return setTabHash(action.payload);
      case 'archiveProgram':
        setOpenModal({ type: 'none' });
        return model.dispatch(action);
      case 'publishProgram':
        return model.dispatch(action);
      case 'reorder':
        return model.dispatch(action);
      case 'closeGuide':
      case 'closeSelectionModal':
      case 'closeModal':
        setOpenModal({ type: 'none' });
        return null;
      case 'selectSub':
        setOpenModal({ type: 'openSelectionModal' });
        return null;
      case 'openGuide':
      case 'subLinksModal':
        return null;
      case 'openProgramUploadModal':
        setOpenModal({ type: 'programUploadModal' });
        return null;
      case 'linksModal':
      case 'archiveModal':
      case 'deleteModal':
        setOpenModal({ type: action.type, value: action.payload as any });
        return null;
      case 'duplicateProgram':
        return p.onDuplicateProgram(action.payload);
      case 'retryDuplication':
        return p.onDuplicateProgram(0);
      case 'deleteProgram':
        return model.dispatch(action);
      case 'subscriptionSettingsModal':
        return setOpenModal({ type: 'subscriptionSettingsModal' });
      case 'closeSubscriptionSettingsModal':
        return exclusiveModel.unsavedChanges
          ? setOpenConfirmationModal(true)
          : setOpenModal({ type: 'none' });
      case 'closeConfirmExitModal':
        return setOpenConfirmationModal(false);
      case 'confirmExitModal':
        setOpenModal({ type: 'none' });
        return setOpenConfirmationModal(false);
      case 'openInitialPriceModal':
        setOpenModal({ type: 'initialPriceModal' });
        return null;
      case 'uploadProgram':
        p.programUploadModel.setFile(action.payload.file);
        p.programUploadModel.upload(true);
        return;
      case 'confirmProgramUpload':
        return p.programUploadModel.upload(false);
    }
  };

export type ToTablePropsParams = {
  selectedList: CollectionsViewState['selectedList'];
  model: CollectionsModel;
  dispatch: CollectionViewDispatcher;
  isCreatorLive: boolean;
  publishDisabled: boolean;
};

export type ToViewModeParams = Omit<ToTablePropsParams, 'isAdmin'>;

export type ModalType =
  | Typed<'none'>
  | Typed<'openGuide'>
  | Typed<'openSelectionModal'>
  | Typed<'openPriceModal'>
  | Typed<'subLinksModal'>
  | Typed<'programUploadModal'>
  | Typed<'subscriptionSettingsModal'>
  | Typed<'initialPriceModal'>
  | TypedValue<'linksModal', LiteCollection>
  | TypedValue<'archiveModal', number>
  | TypedValue<'deleteModal', number>;

export type ViewState = {
  openModal: ModalType;
  openConfirmationModal: boolean;
};

export const useCollectionsViewModel = (
  p: UseCollectionsModelParams,
): Async<CollectionsViewModel> => {
  const openSettings = !!p?.locationSearch?.includes('openSettings');
  const [viewState, setViewState] = useState<ViewState>({
    openModal: { type: openSettings ? 'subscriptionSettingsModal' : 'none' },
    openConfirmationModal: false,
  });
  const { tabHash, setTabHash } = useTabHash(optionTypes, 'all');
  const asyncModel = useCollectionsModel(p);
  const duplicateVM = useCollectionDuplicationVM(asyncModel);
  const asyncExclusiveModel = useExclusiveViewModel(p);
  const programUploadModel = useUploadProgramModel(Number(p.influencerId));

  const setOpenModal = (modal: ModalType) =>
    setViewState((prev) => ({ ...prev, openModal: modal }));

  const setOpenConfirmationModal = (open: boolean) =>
    setViewState((prev) => ({ ...prev, openConfirmationModal: open }));

  if (asyncModel.type !== 'fetched') return asyncModel;
  if (asyncExclusiveModel.type !== 'fetched') return asyncExclusiveModel;
  if (duplicateVM.type !== 'fetched') return duplicateVM;

  const model = asyncModel.value;
  const exclusiveModel = asyncExclusiveModel.value;

  const dispatch = getDispatcher({
    asyncModel,
    asyncExclusiveModel,
    setTabHash,
    setOpenModal,
    onDuplicateProgram: duplicateVM.value.lazyDispatch('DuplicateProgram'),
    setOpenConfirmationModal,
    programUploadModel,
  });
  const createLink = PATHS.NEW_PROGRAM(model.influencerId, {
    salesType: p.salesType,
    from: getCurrentPath(true),
  });
  const isCreatorLive =
    model.isCreatorLive && model.collections.published.length > 0;
  const publishDisabled =
    p.salesType === 'subscription' &&
    model.isCreatorLive &&
    model.isCreatorExclusive &&
    !model.isSubscriptionPriceSet;

  return {
    type: 'fetched',
    value: {
      listOptions: toListOptions(),
      dispatch,
      selectedList: tabHash,
      mode: toCollectionsMode({
        model,
        dispatch,
        selectedList: tabHash,
        isCreatorLive,
        publishDisabled,
      }),
      programDuplication: {
        hasError: duplicateVM.value.hasDuplicationFailed,
      },
      influencerId: model.influencerId,
      createLink,
      emptyState: getEmptyState(
        model,
        exclusiveModel,
        dispatch,
        viewState.openModal,
      ),
      showCreate: model.hasAnyCollection,
      selectionModal: {
        open: viewState.openModal.type === 'openSelectionModal',
        close: () => dispatch({ type: 'closeSelectionModal' }),
      },
      subscription: {
        exclusive: {
          ...toExclusiveSettingsView({
            ...exclusiveModel,
            salesType: p.salesType,
          }),
          promotionsPath: PATHS.PROMOTIONS(model.influencerId),
        },
      },
      linksModal: {
        isOpen: viewState.openModal.type === 'linksModal',
        onClose: () => dispatch({ type: 'closeModal' }),
        data: getProgramLinks(viewState.openModal),
      },
      subLinksModal: {
        showButton: isCreatorLive,
        isOpen: viewState.openModal.type === 'subLinksModal',
        onClose: () => dispatch({ type: 'closeModal' }),
        data: getSubLinks(model.customURL, model.isCreatorExclusive),
      },
      programUploadModal: {
        isOpen: viewState.openModal.type === 'programUploadModal',
        onClose: () => {
          programUploadModel.reset();
          dispatch({ type: 'closeModal' });
        },
        onDrop: (file) =>
          dispatch({ type: 'uploadProgram', payload: { file } }),
        onSubmit: () => dispatch({ type: 'confirmProgramUpload' }),
        data: {
          step: programUploadModel.step,
          uploadSummary: programUploadModel.uploadSummary,
        },
      },
      archiveModal: {
        isOpen: viewState.openModal.type === 'archiveModal',
        close: () => dispatch({ type: 'closeModal' }),
        archive: () =>
          dispatch({
            type: 'archiveProgram',
            payload:
              viewState.openModal.type === 'archiveModal'
                ? viewState.openModal.value
                : 0,
          }),
      },
      deleteModal: {
        isOpen: viewState.openModal.type === 'deleteModal',
        close: () => dispatch({ type: 'closeModal' }),
        onDelete: () =>
          dispatch({
            type: 'deleteProgram',
            payload:
              viewState.openModal.type === 'deleteModal'
                ? viewState.openModal.value
                : 0,
          }),
      },
      confirmExitModal: {
        isOpen: viewState.openConfirmationModal,
        close: () => dispatch({ type: 'closeConfirmExitModal' }),
        cancel: () => dispatch({ type: 'confirmExitModal' }),
      },
      subscriptionSettingsModal: {
        isOpen: viewState.openModal.type === 'subscriptionSettingsModal',
        close: () => dispatch({ type: 'closeSubscriptionSettingsModal' }),
      },
      showSubscriptionSettings:
        p.salesType === 'subscription' &&
        exclusiveModel.showSubscriptionSettings,
      showExperimentalProgramUpload: (
        import.meta.env.VITE_EXPERIMENTAL_FEATURES_USERS || ''
      )
        .split(',')
        .includes(auth.getUser().email),
    },
  };
};
